import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Sprout } from 'lucide-react';
import Button from '../ui/Button';
import fertiliserService from '../../services/fertiliser';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { RATE_UNITS, round } from '../../utils/fertiliser';
import type { FertiliserProduct, FertRateUnit, JobProduct, JobProducts } from '../../types/fertiliser';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm';

function UsageForm({ p, data, onSaved }: { p: JobProduct; data: JobProducts; onSaved: (d: JobProducts) => void }) {
  const held = data.levels.filter((l) => l.chemical_id === p.chemical_id && l.current_stock > 0);
  const [qty, setQty] = useState(p.used_quantity != null ? String(p.used_quantity) : p.planned_quantity != null ? String(p.planned_quantity) : '');
  const [unit, setUnit] = useState(p.used_unit ?? p.planned_unit ?? 'kg');
  const [store, setStore] = useState(String(p.location_id ?? held[0]?.location_id ?? data.locations[0]?.id ?? ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const q = Number(qty);
    if (!(q > 0) || !store) { setError('Give the amount used and the store it came from.'); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await fertiliserService.recordUsage(p.id, { used_quantity: q, used_unit: unit, location_id: Number(store) });
      if (!r.success || !r.data) throw r;
      onSaved(r.data);
    } catch (e) {
      setError(errorMessage(e, 'Could not record it'));
    } finally {
      setBusy(false);
    }
  };

  if (!data.locations.length) return <p className="text-xs text-amber-800">There is no store to take it from. Add one on Fertiliser → Stock.</p>;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_5rem] sm:grid-cols-[8rem_5rem_1fr_auto] gap-2 items-end">
        <label className="block text-sm">
          <span className="text-neutral-700">Used</span>
          <input type="text" inputMode="decimal" className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-neutral-700">Unit</span>
          <select className={inputCls} value={unit} onChange={(e) => setUnit(e.target.value)}>
            {['kg', 'g', 'L', 'mL'].map((u) => <option key={u}>{u}</option>)}
          </select>
        </label>
        <label className="block text-sm col-span-2 sm:col-span-1">
          <span className="text-neutral-700">From store</span>
          <select className={inputCls} value={store} onChange={(e) => setStore(e.target.value)}>
            {data.locations.map((l) => {
              const h = data.levels.find((x) => x.chemical_id === p.chemical_id && x.location_id === l.id);
              return <option key={l.id} value={l.id}>{l.name}{h ? ` (${round(h.current_stock, 1)} ${h.unit})` : ''}</option>;
            })}
          </select>
        </label>
        <Button size="sm" variant="primary" isLoading={busy} onClick={save} className="col-span-2 sm:col-span-1">
          {p.recorded_at ? 'Record again' : 'Record used'}
        </Button>
      </div>
      {error && <p className="text-xs text-error-700">{error}</p>}
    </div>
  );
}

/**
 * The fertiliser a job puts on: rate and the amount planned, and once the
 * work is done, what was used and from which store, which takes it off
 * stock. Shown on a fertilising job, or any job that carries a product.
 */
export default function JobProductsCard({ jobId, fertilising }: { jobId: number; fertilising: boolean }) {
  const timezone = useOrgTimezone();
  const { hasAccess: canRecord } = useModuleLicense('farm_jobs', ['supervisor', 'creator', 'admin']);
  const { hasAccess: canAdd } = useModuleLicense('farm_jobs', ['creator', 'admin']);
  const [data, setData] = useState<JobProducts | null>(null);
  const [products, setProducts] = useState<FertiliserProduct[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [add, setAdd] = useState({ chemical_id: '', rate: '', rate_unit: 'kg/ha' as FertRateUnit });
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fertiliserService.jobProducts(jobId)
      .then((r) => { if (r.success && r.data) setData(r.data); })
      .catch((e) => setError(errorMessage(e, 'Could not load the job\'s products')));
  }, [jobId]);
  useEffect(load, [load]);

  const openAdd = async () => {
    setAdding(true);
    if (!products) {
      const r = await fertiliserService.products().catch(() => null);
      setProducts(r?.success && r.data ? r.data : []);
    }
  };

  const submitAdd = async () => {
    if (!add.chemical_id || !(Number(add.rate) > 0)) { setError('Choose the fertiliser and give a rate.'); return; }
    setError(null);
    try {
      const r = await fertiliserService.addJobProduct(jobId, { chemical_id: Number(add.chemical_id), rate: Number(add.rate), rate_unit: add.rate_unit });
      if (r.success && r.data) { setData(r.data); setAdding(false); setAdd({ chemical_id: '', rate: '', rate_unit: 'kg/ha' }); }
    } catch (e) {
      setError(errorMessage(e, 'Could not add it'));
    }
  };

  // Nothing at all on a job that is not fertilising until it is known to carry a product.
  if (!data) {
    if (!fertilising) return null;
    return error ? <p className="text-sm text-error-700">{error}</p> : <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>;
  }
  if (!data.products.length && !fertilising) return null;

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-700 flex items-center gap-2"><Sprout className="h-4 w-4 text-neutral-500" /> Fertiliser</h3>
        {canAdd && !adding && (
          <Button size="sm" variant="outline" onClick={openAdd}><Plus className="h-4 w-4" /> Add a fertiliser</Button>
        )}
      </div>
      {error && <p className="text-sm text-error-700">{error}</p>}
      {data.products.length === 0 && !adding && <p className="text-sm text-neutral-500">No fertiliser on this job yet.</p>}

      {adding && (
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_6rem_auto] gap-2 items-end">
          <label className="block text-sm">
            <span className="text-neutral-700">Fertiliser</span>
            <select className={inputCls} value={add.chemical_id} onChange={(e) => setAdd((a) => ({ ...a, chemical_id: e.target.value }))}>
              <option value="">Choose</option>
              {products?.map((p) => <option key={p.id} value={p.id}>{p.product_name}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-neutral-700">Rate</span>
            <input type="text" inputMode="decimal" className={inputCls} value={add.rate} onChange={(e) => setAdd((a) => ({ ...a, rate: e.target.value }))} />
          </label>
          <label className="block text-sm">
            <span className="text-neutral-700">per</span>
            <select className={inputCls} value={add.rate_unit} onChange={(e) => setAdd((a) => ({ ...a, rate_unit: e.target.value as FertRateUnit }))}>
              {RATE_UNITS.map((u) => <option key={u}>{u}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <Button size="sm" variant="primary" onClick={submitAdd}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <ul className="divide-y divide-neutral-100">
        {data.products.map((p) => (
          <li key={p.id} className="py-3 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
              <span className="font-medium text-neutral-900 break-words min-w-0">{p.product_name}</span>
              <span className="text-neutral-600">
                {p.rate != null && `${round(p.rate, 2)} ${p.rate_unit}`}
                {p.planned_quantity != null && ` · about ${round(p.planned_quantity, 1)} ${p.planned_unit} in all`}
              </span>
            </div>
            {p.recorded_at && (
              <p className="text-xs text-green-800">
                Used {round(p.used_quantity, 1)} {p.used_unit} from {p.location_name ?? 'a store'}
                {p.recorded_by_name && `, recorded by ${p.recorded_by_name}`} {formatDateTimeInTz(p.recorded_at, timezone)}. Taken off stock.
              </p>
            )}
            {canRecord
              ? <UsageForm p={p} data={data} onSaved={setData} />
              : !p.recorded_at && <p className="text-xs text-neutral-500">A Farm Jobs Supervisor, Creator or Admin records what was used.</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
